/**
 * @file src/shell/LoadingScreen.js
 * Màn hình tải. Nâng từ three-lessons/src/ui/LoadingScreen.js.
 * Giữ MIN_VISIBLE_MS để không nháy khi asset đã cache (chi tiết quan trọng về UX).
 */

const MIN_VISIBLE_MS = 700;

/** Thời gian lớp phủ mờ ra. PHẢI khớp `transition` của .ol-loading trong shell.css —
 * hai số này là một cặp: hide() chờ đúng khoảng này rồi mới resolve, ngắn hơn CSS thì
 * markReady() chạy khi lớp phủ còn đang mờ. 600ms (không phải 350ms như bản trước): lớp
 * phủ nền #0d1526 rất tối còn sân khấu #f9fafb rất sáng, mờ ra trong 350ms là một cú
 * nhảy sáng đọc ra thành "nhấp nháy" (yêu cầu review). */
const FADE_MS = 600;

/** Logo thương hiệu — asset cấp SHELL trong _shared, dùng chung mọi tiết. */
const LOGO_PATH = 'assets/_shared/icon/edmicro-logo.webp';

/**
 * @typedef {Object} LoadingApi
 * @property {(o: {title?: string, subtitle?: string, backgroundUrl?: string,
 *   baseUrl?: string}) => void} show
 * @property {(p: number) => void} setProgress  0..1
 * @property {(s: string) => void} setStatus
 * @property {() => Promise<void>} hide
 * @property {() => void} dispose
 */

/**
 * @param {HTMLElement} root
 * @returns {LoadingApi}
 */
export function createLoadingScreen(root) {
  const el = document.createElement('div');
  el.className = 'ol-loading';
  el.innerHTML = `
    <div class="ol-loading__bg"></div>
    <div class="ol-loading__box">
      <img class="ol-loading__logo" alt="Edmicro 3D" decoding="async">
      <div class="ol-loading__title"></div>
      <div class="ol-loading__subtitle"></div>
      <div class="ol-loading__bar"><i></i></div>
      <div class="ol-loading__status"></div>
    </div>`;
  root.appendChild(el);

  const q = (/** @type {string} */ s) => /** @type {HTMLElement} */ (el.querySelector(s));
  const bg = q('.ol-loading__bg');
  const logo = /** @type {HTMLImageElement} */ (q('.ol-loading__logo'));
  const title = q('.ol-loading__title');
  const subtitle = q('.ol-loading__subtitle');
  const bar = q('.ol-loading__bar > i');
  const status = q('.ol-loading__status');

  let shownAt = 0;

  return {
    show(o) {
      shownAt = performance.now();
      title.textContent = o.title ?? '';
      subtitle.textContent = o.subtitle ?? '';
      // Poster: nạp trước rồi mới gán, để nó MỜ DẦN vào (CSS transition trên
      // .ol-loading__bg) chứ không hiện phựt giữa lúc đang tải.
      if (o.backgroundUrl) {
        const pre = new Image();
        pre.onload = () => {
          bg.style.backgroundImage = `url("${o.backgroundUrl}")`;
          bg.classList.add('is-loaded');
        };
        pre.src = o.backgroundUrl;
      }
      // Logo có thể thiếu (repo mới clone chưa có _shared) → ẩn thẻ img chứ đừng
      // để hiện icon ảnh lỗi giữa màn hình tải.
      logo.onerror = () => logo.classList.add('is-missing');
      logo.src = `${o.baseUrl ?? '/'}${LOGO_PATH}`.replace(/([^:])\/{2,}/g, '$1/');
      bar.style.width = '0%';
      el.classList.add('is-visible');
    },
    setProgress(p) {
      bar.style.width = `${Math.round(Math.max(0, Math.min(1, p)) * 100)}%`;
    },
    setStatus(s) {
      status.textContent = s;
    },
    /**
     * Mờ lớp phủ ra. ĐÂY LÀ TOÀN BỘ nhịp mở bài — cảnh 3D phía sau đã render xong nên
     * lớp phủ mờ đi là lộ ra một cảnh hoàn chỉnh, không cần (và không nên) fade thêm mô
     * hình. Xem mục "Nhịp MỞ BÀI" trong shell.css.
     */
    async hide() {
      const wait = Math.max(0, MIN_VISIBLE_MS - (performance.now() - shownAt));
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      bar.style.width = '100%';
      el.classList.remove('is-visible');
      await new Promise((r) => setTimeout(r, FADE_MS));
    },
    dispose() {
      el.remove();
    },
  };
}
